import React, { useState, useEffect } from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { linearToLog, logToLinear, formatLogValue } from "~/lib/utils/logarithmic";

interface RangeSliderProps {
  value: number;
  valueMin: number;
  valueMax: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  decimals?: number; // Number of decimal places to display
  logarithmic?: boolean; // Use logarithmic scale for finer control at low values
  onChange?: (value: number, valueMin: number, valueMax: number) => void;
  onPointerDown?: () => void;
  onPointerUp?: () => void;
  disabled?: boolean;
  scrambleEnabled?: boolean; // If true, highlights min/max thumbs; if false, greys them out
  className?: string;
}

/**
 * RangeSlider component with three thumbs (min, current, max).
 * Allows setting a value and constraining scrambling to a min/max range.
 *
 * The three thumbs represent:
 * - Min thumb: Minimum value for scrambling
 * - Current thumb: Current value
 * - Max thumb: Maximum value for scrambling
 *
 * Thumbs cannot overlap - min <= current <= max is always maintained.
 *
 * Used in Pattern.tsx for Range, Spread, Space, and Dissonance parameters.
 */
export const RangeSlider = React.forwardRef<HTMLDivElement, RangeSliderProps>(
  (
    {
      value,
      valueMin,
      valueMax,
      min,
      max,
      step = 1,
      unit = "",
      decimals,
      logarithmic = false,
      onChange,
      onPointerDown,
      onPointerUp,
      disabled,
      scrambleEnabled = true,
      className,
    },
    forwardedRef
  ) => {
    // Convert logarithmic values to linear for slider if needed
    const toSliderValue = (val: number) => (logarithmic ? logToLinear(val) : val);
    const fromSliderValue = (val: number) => (logarithmic ? linearToLog(val) : val);

    // Track current values for display during drag (in log space if logarithmic)
    // Array order: [min, current, max]
    const [currentValues, setCurrentValues] = useState<[number, number, number]>([
      valueMin,
      value,
      valueMax,
    ]);

    // Sync internal state when controlled values change
    useEffect(() => {
      setCurrentValues([valueMin, value, valueMax]);
    }, [valueMin, value, valueMax]);

    const handleValueChange = (newValues: number[]) => {
      // Radix slider returns array in order: [thumb1, thumb2, thumb3]
      // These are in linear space, need to convert to log if logarithmic
      const [linearMin, linearCurrent, linearMax] = newValues as [number, number, number];

      // Convert from linear slider space to storage space (log if logarithmic)
      const newMin = fromSliderValue(linearMin);
      const newCurrent = fromSliderValue(linearCurrent);
      const newMax = fromSliderValue(linearMax);

      // Update display immediately while dragging
      setCurrentValues([newMin, newCurrent, newMax]);

      // Call parent onChange callback with log values
      onChange?.(newCurrent, newMin, newMax);
    };

    // Format value with specified decimal places
    const formatValue = (val: number): string => {
      // Handle undefined/null values
      if (val === undefined || val === null || isNaN(val)) {
        return decimals !== undefined ? "0.0" : "0";
      }

      // Use logarithmic formatter if enabled
      if (logarithmic) {
        return formatLogValue(val);
      }

      if (decimals !== undefined) {
        return val.toFixed(decimals);
      }
      return val.toString();
    };

    return (
      <div ref={forwardedRef} className="w-full space-y-2">
        <SliderPrimitive.Slider
          value={[
            toSliderValue(currentValues[0]),
            toSliderValue(currentValues[1]),
            toSliderValue(currentValues[2]),
          ]}
          min={min}
          max={max}
          step={step}
          onValueChange={handleValueChange}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          disabled={disabled}
          minStepsBetweenThumbs={0}
          className={`relative flex w-full touch-none select-none items-center ${className || ""}`}
        >
          <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-gray-800">
            {/* Range color changes based on scramble state - easy to adjust via these classes */}
            <SliderPrimitive.Range
              className={`absolute h-full transition-colors ${
                scrambleEnabled ? "bg-gray-500" : "bg-gray-700"
              }`}
            />
          </SliderPrimitive.Track>

          {/* Min thumb - greyed out when scramble disabled */}
          <SliderPrimitive.Thumb
            className={`block h-4 w-4 rounded-full border-2 shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-950 disabled:pointer-events-none disabled:opacity-50 ${
              scrambleEnabled
                ? "border-blue-500 bg-blue-300 hover:bg-blue-200 focus:ring-blue-400"
                : "border-gray-600 bg-gray-600 hover:bg-gray-500 focus:ring-gray-500"
            }`}
            aria-label="Minimum value"
          />

          {/* Current value thumb */}
          <SliderPrimitive.Thumb
            className="block h-5 w-5 rounded-full border-2 border-gray-500 bg-gray-300 shadow-lg transition-colors hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 focus:ring-offset-gray-950 disabled:pointer-events-none disabled:opacity-50"
            aria-label="Current value"
          />

          {/* Max thumb - greyed out when scramble disabled */}
          <SliderPrimitive.Thumb
            className={`block h-4 w-4 rounded-full border-2 shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-950 disabled:pointer-events-none disabled:opacity-50 ${
              scrambleEnabled
                ? "border-blue-500 bg-blue-300 hover:bg-blue-200 focus:ring-blue-400"
                : "border-gray-600 bg-gray-600 hover:bg-gray-500 focus:ring-gray-500"
            }`}
            aria-label="Maximum value"
          />
        </SliderPrimitive.Slider>

        <div className="flex justify-between text-xs text-gray-400 font-mono">
          <output>{formatValue(currentValues[0])}</output>
          <output className="text-sm font-semibold text-gray-200">
            {formatValue(currentValues[1])}
            {unit}
          </output>
          <output>{formatValue(currentValues[2])}</output>
        </div>
      </div>
    );
  }
);

RangeSlider.displayName = "RangeSlider";

export default RangeSlider;
